
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  ColumnDef as TanStackColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import React, { Fragment } from "react";
import { Button, Row, Table } from "react-bootstrap";
import ProjectWarehouseFilterPopover from "./ProjectWarehouseFilterPopover";
import DateFilterPopover from "./DateFilterPopover";
import DateRangeFilterPopover from "./DateRangeFilterPopover";

type FilterType = "dropdown" | "text" | "date" | "boolean" | "singleDate";

export type CustomColumnDef<TData> = TanStackColumnDef<TData> & {
  filterType?: FilterType;
  filterOptions?: string[];
  filterFetchUrl?: string;
};

interface TableContainerProps<TData = any> { 
  columns: CustomColumnDef<TData>[];
  data: TData[];
  totalRecords: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  colValue: any;
  isBordered?: boolean;
  isPagination?: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  manualSorting: boolean;
  isGlobalFilter?: boolean;
  SearchPlaceholder?: string;
  onTableReady?: (tableInstance: any) => void;
}

const ProjectWarehouseTable = ({
  columns,
  data,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  setColumnFilters,
  sorting,
  onSortingChange,
  manualSorting = true,
  isBordered = false,
  isPagination = true,
  colValue,
  onTableReady,
}: TableContainerProps) => {
  const handleColumnFilterChange: OnChangeFn<ColumnFiltersState> = (
    updaterOrValue
  ) => {
    if (!setColumnFilters) return;
    setColumnFilters((prev: any) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue
    );
  };

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualFiltering: true,
    manualSorting,
    pageCount: Math.ceil(totalRecords / pageSize),
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
      columnFilters,
    },
    onSortingChange,
    onColumnFiltersChange: handleColumnFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: !manualSorting ? getSortedRowModel() : undefined,
  });

  // Use ref to track if table instance has been set to prevent unnecessary updates
  const hasSetTableInstance = React.useRef(false);
  const prevColumnsLength = React.useRef(columns.length);
  
  // Call onTableReady when table is ready, but only when columns change or on initial mount
  React.useEffect(() => {
    if (onTableReady) {
      // Update if columns length changed (new columns added/removed) or if this is the first time
      if (!hasSetTableInstance.current || prevColumnsLength.current !== columns.length) {
        hasSetTableInstance.current = true;
        prevColumnsLength.current = columns.length;
        onTableReady(table);
      }
    }
  }, [columns.length, onTableReady, table]);

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
  } = table;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const maxPageButtons = 5;

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= maxPageButtons) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      if (pageIndex <= 2) pages.push(0, 1, 2, -1, totalPages - 1);
      else if (pageIndex >= totalPages - 3)
        pages.push(0, -1, totalPages - 3, totalPages - 2, totalPages - 1);
      else
        pages.push(
          0,
          -1,
          pageIndex - 1,
          pageIndex,
          pageIndex + 1,
          -1,
          totalPages - 1
        );
    }
    return pages;
  };


  return (
    <Fragment>
      <style>
        {`
          .sticky-column {
            width:40px;
            position: sticky;
            right: 0;
            z-index: 1;
            background-color: #fff !important;
            border-left: 1px solid #dee2e6;
            text-align: center;
          }
          thead .sticky-column {
            background-color: #EEF0F7 !important;
          }
          .sticky-action {
            box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
          }
          .table td,
          .table th {
            background-clip: padding-box;
          }
          .remark-cell {
            max-width: 300px;
            white-space: normal !important;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
        `}
      </style>

      <div className="table-responsive table-card">
        <Table
          className={`table table-centered align-middle table-custom-effect table-nowrap mb-0 ${isBordered ? "table-bordered" : ""
            }`}
          bordered={isBordered}
        >
          <thead className="table-light">
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn = index === headerGroup.headers.length - 1;
                  const isSorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const isRemark =
                    header.column.columnDef.header === "Remark";

                  const sortIcon =
                    isSorted === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : isSorted === "desc" ? (
                      <ArrowDownwardIcon fontSize="small" />
                    ) : (
                      <UnfoldMoreIcon fontSize="small" />
                    );

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`align-middle ${isLastColumn ? "sticky-column sticky-action" : ""
                        } ${isRemark ? "remark-cell" : ""}`}
                      style={
                        isRemark
                          ? {
                            minWidth: "180px",
                            maxWidth: "300px",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }
                          : undefined
                      }
                    >
                      {!header.isPlaceholder && (
                        <div
                          className="d-flex justify-content-between align-items-center"
                          style={{ gap: 4 }}
                        >
                          <div
                            className="d-flex align-items-center"
                            style={{
                              gap: 4,
                              cursor: canSort ? "pointer" : "default",
                            }}
                            onClick={
                              canSort
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            <span>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            {canSort && sortIcon}
                          </div>
                          {/* 
                          {header.column.getCanFilter() ? (
                            (header.column.columnDef as CustomColumnDef<any>)
                              .filterType === "date" ? (
                              <DateRangeFilterPopover
                                column={header.column}
                                setColValue={colValue}
                                iconColor={
                                  header.column.getFilterValue()
                                    ? "#3258d3"
                                    : "inherit"
                                }
                              />
                            ) : (
                              <ProcurementFilterPopover
                                column={header.column}
                                columnDef={header.column.columnDef}
                                columnFilters={columnFilters}
                                setColumnFilters={setColumnFilters}
                                setColValue={colValue}
                                iconColor={
                                  header.column.getFilterValue()
                                    ? "#3258d3"
                                    : "inherit"
                                }
                              />
                            )
                          ) : null} */}



                          {header.column.getCanFilter() ? (
                            (() => {
                              const filterType = (header.column.columnDef as CustomColumnDef<any>).filterType;
                              if (filterType === "date") {
                                return (
                                  <DateRangeFilterPopover
                                    column={header.column}
                                    setColValue={colValue}
                                    iconColor={
                                      header.column.getFilterValue() ? "#3258d3" : "inherit"
                                    }
                                  />
                                );
                              }

                              if (filterType === "singleDate") {
                                return (
                                  <DateFilterPopover
                                    column={header.column}
                                    setColValue={colValue}
                                    iconColor={
                                      header.column.getFilterValue() ? "#3258d3" : "inherit"
                                    }
                                  />
                                );
                              }

                              return (
                                <ProjectWarehouseFilterPopover
                                  column={header.column}
                                  columnDef={header.column.columnDef}
                                  columnFilters={columnFilters}
                                  setColumnFilters={setColumnFilters}
                                  setColValue={colValue}
                                  iconColor={
                                    header.column.getFilterValue() ? "#3258d3" : "inherit"
                                  }
                                />
                              );
                            })()
                          ) : null}

                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No Data Found
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index) => {
                    const isLastColumn =
                      index === row.getVisibleCells().length - 1;
                    const isRemark =
                      cell.column.columnDef.header === "Remark";

                    return (
                      <td
                        key={cell.id}
                        className={`${isLastColumn ? "sticky-column sticky-action" : ""
                          } ${isRemark ? "remark-cell" : ""}`}
                        style={
                          isRemark
                            ? {
                              maxWidth: "300px",
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                            }
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      
      {isPagination && (
        <Row className="align-items-center mt-4 pt-3 gy-2">
          <div className="col-sm">
            <div className="text-muted">
              Showing{" "}
              <span className="fw-semibold">
                {totalRecords === 0 ? 0 : pageIndex * pageSize + 1}
              </span>
              {" - "}
              <span className="fw-semibold">
                {Math.min((pageIndex + 1) * pageSize, totalRecords)}
              </span>
              {" of "}
              <span className="fw-semibold">{totalRecords}</span> Results
            </div>

          </div>
          <div className="col-sm-auto">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <ul className="pagination pagination-separated mb-0">
                <li
                  className={`page-item ${!getCanPreviousPage() ? "disabled" : ""
                    }`}
                >
                  <Button
                    variant="link"
                    className="page-link"
                    disabled={pageIndex === 0}
                    onClick={() => onPageChange(pageIndex - 1)}
                  >
                    &lt;
                  </Button>
                </li>
                {getPageNumbers().map((page, idx) => (
                  <li className="page-item" key={idx}>
                    {page === -1 ? (
                      <span className="page-link">...</span>
                    ) : (
                      <Button
                        variant="link"
                        className={`page-link ${pageIndex === page ? "active" : ""
                          }`}
                        onClick={() => onPageChange(page)}
                      >
                        {page + 1}
                      </Button>
                    )}
                  </li>
                ))}
                <li
                  className={`page-item ${!getCanNextPage() ? "disabled" : ""
                    }`}
                >
                  <Button
                    variant="link"
                    className="page-link"
                    disabled={
                      pageIndex >= Math.ceil(totalRecords / pageSize) - 1
                    }
                    onClick={() => onPageChange(pageIndex + 1)}
                  >
                    &gt;
                  </Button>
                </li>
              </ul>

              <div className="ms-2">
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: "100px" }}
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                >
                  {[10, 20, 30, 50].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

export default ProjectWarehouseTable;
